import React, { useEffect, useState, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import './HeroContainer.css';
import {
  ArrowRight,
  Code2,
  Menu,
  Phone,
  RadioTower,
  SunMedium,
  X,
  Globe,
  MapPin,
  ChevronDown,
  CheckCircle2
} from 'lucide-react';
import Logo from '../logo.png';

const navigation = [
  { href: '#home', label: 'Início' },
  { href: '#services', label: 'Especialidades' },
  { href: '#approach', label: 'Abordagem' },
  { href: '#contact', label: 'Contacto' }
];

const heroMetrics = [
  { value: '360°', label: 'Coordenação técnica integrada' },
  { value: '3', label: 'Áreas de especialização ligadas entre si' },
  { value: '24h', label: 'Janela típica para resposta inicial' },
  { value: 'PT', label: 'Proximidade local, linguagem empresarial' }
];

const services = [
  {
    id: 'digital',
    icon: Code2,
    title: 'Software e presença digital com foco comercial',
    description: 'Criamos produtos e plataformas que ajudam a vender melhor, operar com mais clareza e escalar sem perder controlo.',
    gridSpan: 'col-span-12 md:col-span-8',
    features: ['Websites e plataformas responsivas', 'Ferramentas internas e integrações API', 'Experiências orientadas à conversão', 'Automação de processos']
  },
  {
    id: 'energia',
    icon: SunMedium,
    title: 'Soluções solares desenhadas para retorno',
    description: 'Ligamos engenharia, implementação e monitorização para reduzir o custo energético e aumentar a previsibilidade.',
    gridSpan: 'col-span-12 md:col-span-4',
    features: ['Dimensionamento técnico', 'Monitorização e leitura', 'Planos de eficiência']
  },
  {
    id: 'telecom',
    icon: RadioTower,
    title: 'Infraestrutura de conectividade crítica',
    description: 'Desenhamos e executamos redes empresariais e infraestruturas críticas com máxima atenção à performance e redundância.',
    gridSpan: 'col-span-12',
    features: ['Fibra e redes empresariais', 'Soluções wireless e backbone', 'Operação e manutenção técnica']
  }
];

const processSteps = [
  {
    num: '01',
    title: 'Diagnóstico claro',
    desc: 'Levantamos o contexto, as prioridades, as restrições e o impacto no negócio antes de propor qualquer solução.'
  },
  {
    num: '02',
    title: 'Plano executável',
    desc: 'Traduzimos a estratégia global em arquitetura, cronograma, investimento e entregáveis concretos.'
  },
  {
    num: '03',
    title: 'Entrega e operação',
    desc: 'Implementamos e acompanhamos a adoção do sistema, deixando uma base técnica preparada para evoluir a longo prazo.'
  }
];



const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.nav 
      className={`premium-nav ${scrolled ? 'nav-scrolled' : ''}`}
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
    >
      <div className="nav-container">
        <a href="#home" className="nav-brand" aria-label="Ir para o início">
          <img src={Logo} alt="Logótipo NEXteam" className="brand-logo" width="150" height="40" />
        </a>

        <nav className="nav-desktop" aria-label="Navegação Principal">
          {navigation.map((item) => (
            <a key={item.href} href={item.href} className="nav-item">
              {item.label}
            </a>
          ))}
          <a href="#contact" className="nav-btn-primary">
            Falar Connosco
          </a>
        </nav>

        <button className="nav-mobile-toggle" onClick={() => setMenuOpen(!menuOpen)} aria-label={menuOpen ? "Fechar menu oculto" : "Abrir menu de navegação"} aria-expanded={menuOpen}>
          {menuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav 
            className="mobile-menu glass-panel-heavy"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            aria-label="Navegação Mobile"
          >
            {navigation.map((item) => (
              <a 
                key={item.href} 
                href={item.href} 
                className="mobile-nav-item"
                onClick={() => setMenuOpen(false)}
              >
                {item.label}
              </a>
            ))}
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

const BentoCard = ({ service, index }) => {
  const Icon = service.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
      className={`bento-card glass-panel ${service.gridSpan}`}
    >
      <div className="bento-glow" />
      <div className="bento-content">
        <div className="bento-icon-wrapper" aria-hidden="true">
          <Icon size={28} strokeWidth={1.5} />
        </div>
        <h3 className="bento-title">{service.title}</h3>
        <p className="bento-desc">{service.description}</p>
        
        <ul className="bento-features" aria-label={`Destaques de ${service.title}`}>
          {service.features.map((feature, idx) => (
            <li key={idx}><CheckCircle2 size={16} aria-hidden="true" /> <span>{feature}</span></li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
};

function HeroContainer() {
  const containerRef = useRef(null);
  const [cookieModalOpen, setCookieModalOpen] = useState(false);
  const [showCookieBanner, setShowCookieBanner] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('nexteam_cookie_consent');
    if (!consent) {
      setShowCookieBanner(true);
    }
  }, []);

  const acceptCookies = () => {
    localStorage.setItem('nexteam_cookie_consent', 'true');
    setShowCookieBanner(false);
  };

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  const heroY = useTransform(scrollYProgress, [0, 0.2], [0, 150]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);

  return (
    <div className="apple-page" ref={containerRef}>
      <Navbar />

      <main>
        {/* HERO SECTION */}
        <section className="section-hero" id="home">
          <motion.div 
            className="hero-content-wrapper"
            style={{ y: heroY, opacity: heroOpacity }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
              className="hero-badge"
            >
              <div className="badge-dot" />
              <span>Engenharia, energia e sistemas digitais</span>
            </motion.div>

            <motion.h1 
              className="hero-headline"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            >
              Infraestrutura <br className="desktop-break" />
              e <span className="text-gradient">soluções digitais.</span>
            </motion.h1>

            <motion.p 
              className="hero-subheadline"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            >
              Ajudamos empresas a unir software, energia e conectividade numa operação mais robusta, mais clara e pronta para crescer sem complexidade desnecessária.
            </motion.p>

            <motion.div 
              className="hero-cta-group"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
            >
              <a href="#contact" className="btn-primary-large" aria-label="Falar com a NEXTEAM para iniciar projeto">
                Falar com a NEXTEAM <ArrowRight size={20} aria-hidden="true" />
              </a>
            </motion.div>
          </motion.div>
        </section>

        {/* METRICS SECTION OVERLAY */}
        <section className="section-metrics">
          <div className="metrics-container">
            {heroMetrics.map((metric, i) => (
              <motion.div 
                key={metric.value}
                className="metric-item glass-panel"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
              >
                <div className="metric-value">{metric.value}</div>
                <div className="metric-label">{metric.label}</div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* BENTO GRID (SERVICES) */}
        <section className="section-bento" id="services">
          <div className="bento-container">
            <motion.div 
              className="section-header center"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="section-title">Três áreas técnicas. <br/>Uma experiência coerente.</h2>
              <p className="section-subtitle">Em vez de fornecedores desalinhados, damos-lhe uma frente técnica integrada com foco em imagem, eficiência e continuidade.</p>
            </motion.div>

            <div className="bento-grid">
              {services.map((service, i) => (
                <BentoCard key={service.id} service={service} index={i} />
              ))}
            </div>
          </div>
        </section>

        {/* NEW PROCESS SECTION */}
        <section className="section-process" id="approach">
          <div className="process-container">
            <motion.div 
              className="section-header center"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="section-title">O nosso <span className="text-gradient">método.</span></h2>
              <p className="section-subtitle">Longe de burocracias pesadas. Entregamos clareza e previsibilidade desde o primeiro contacto até à operação final.</p>
            </motion.div>

            <div className="process-cards">
              {processSteps.map((step, i) => (
                <motion.div 
                  key={step.num}
                  className="method-card glass-panel"
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.8, delay: i * 0.15 }}
                >
                  <div className="method-num">{step.num}</div>
                  <h3 className="method-title">{step.title}</h3>
                  <p className="method-desc">{step.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* PREMIUM CONTACT SECTION */}
        <section className="section-contact" id="contact">
          <div className="contact-container glass-panel-heavy">
            <div className="contact-grid">
              <div className="contact-info">
                <h2 className="contact-title">Um desafio técnico? Falamos.</h2>
                <p className="contact-desc">
                  Partilhe o objetivo, o contexto e a fase do projeto. Respondemos com uma conversa inicial objetiva e sem ruído.
                </p>
                <div className="contact-channels">
                  <div className="channel">
                    <MapPin size={24} aria-hidden="true" />
                    <span>Rua Bombeiro Abílio Nunes Novo n6A</span>
                  </div>
                  <a href="tel:+351253965288" className="channel" aria-label="Ligar para +351 253 965 288">
                    <Phone size={24} aria-hidden="true" style={{ marginTop: '2px' }} />
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span>+351 253 965 288*</span>
                      <small style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '2px', fontWeight: '400' }}>* chamada para rede fixa nacional</small>
                    </div>
                  </a>
                  <a href="mailto:info@nexteam.pt" className="channel" aria-label="Enviar email para info@nexteam.pt">
                    <Globe size={24} aria-hidden="true" />
                    <span>info@nexteam.pt</span>
                  </a>
                </div>
              </div>

              <div className="contact-form-wrapper">
                <form className="apple-form" action="mailto:info@nexteam.pt" method="post" encType="text/plain" aria-label="Formulário de contacto">
                  <div className="form-group">
                    <input type="text" name="Nome" placeholder="O seu nome / Empresa" required aria-label="Nome ou Empresa" />
                  </div>
                  <div className="form-group">
                    <input type="email" name="Email" placeholder="Email de contacto" required aria-label="Seu Email" />
                  </div>
                  <div className="form-group">
                    <textarea name="Mensagem" placeholder="Explique o contexto e tipo de apoio necessário." rows={4} required aria-label="Mensagem"></textarea>
                  </div>
                  <button type="submit" className="btn-primary-large btn-full" aria-label="Enviar mensagem">
                    Enviar <ArrowRight size={20} aria-hidden="true" />
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

      </main>

      <footer className="apple-footer" role="contentinfo">
        <div className="footer-container">
          <div className="footer-top">
            <img src={Logo} alt="Logótipo NEXteam do Radapé" className="footer-logo" width="120" height="32" loading="lazy" />
            <p className="footer-tagline">Soluções digitais, energia e telecomunicações com foco empresarial.</p>
          </div>
          <div className="footer-bottom">
            <p>© {new Date().getFullYear()} NEXTEAM. Todos os direitos reservados.</p>
            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
              <button className="footer-link-btn" onClick={() => setCookieModalOpen(true)}>Política de Privacidade e Cookies</button>
              <a href="https://www.livroreclamacoes.pt/" target="_blank" rel="noreferrer" className="footer-link-btn">Livro de Reclamações Eletrónico</a>
            </div>
          </div>
        </div>
      </footer>

      <AnimatePresence>
        {cookieModalOpen && (
          <motion.div 
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setCookieModalOpen(false)}
          >
            <motion.div 
              className="modal-content glass-panel-heavy"
              role="dialog"
              aria-modal="true"
              aria-labelledby="cookie-modal-title"
              initial={{ y: 50, opacity: 0, scale: 0.95 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 50, opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3 id="cookie-modal-title">Política de Cookies</h3>
                <button onClick={() => setCookieModalOpen(false)} aria-label="Fechar Política de Cookies"><X size={24} aria-hidden="true" /></button>
              </div>
              <div className="modal-body">
                <p>Utilizamos cookies para melhorar o desempenho e a sua experiência como utilizador no nosso site.</p>
                
                <h4 style={{ color: 'var(--text)', marginTop: '24px', marginBottom: '12px' }}>O que são Cookies?</h4>
                <p>As Cookies são pequenos ficheiros de texto que um site, quando visitado, coloca no computador do utilizador ou no seu dispositivo móvel, através do browser.</p>
                <p>A colocação de Cookies ajudará o site a reconhecer o seu dispositivo numa próxima visita. As cookies retêm apenas informação relacionada com as suas experiências, não incluindo, como tal, os seus dados pessoais.</p>

                <h4 style={{ color: 'var(--text)', marginTop: '24px', marginBottom: '12px' }}>Para que servem as Cookies?</h4>
                <p>As cookies são utilizadas para ajudar a determinar a utilidade, interesse e o número de utilizações dos sites, permitindo uma navegação mais rápida e eficiente e eliminando a necessidade de introduzir repetidamente as mesmas informações.</p>

                <h4 style={{ color: 'var(--text)', marginTop: '24px', marginBottom: '12px' }}>Que tipos de Cookies existem?</h4>
                <p><strong>Cookies permanentes:</strong> Ficam armazenadas ao nível do navegador de internet nos seus dispositivos de acesso e são utilizadas sempre que o utilizador faz uma nova visita ao nosso site. Geralmente são utilizadas para direcionar a navegação de acordo com os interesses do utilizador.</p>
                <p><strong>Cookies de sessão:</strong> São temporárias, permanecem no arquivo de cookies do seu navegador até sair do site. A informação obtida através destas cookies permite identificar problemas e fornecer uma melhor experiência de navegação.</p>

                <h4 style={{ color: 'var(--text)', marginTop: '24px', marginBottom: '12px' }}>Que tipos de Cookies utilizamos?</h4>
                <p><strong>Cookies essenciais:</strong> Algumas cookies são essenciais para aceder a áreas específicas do nosso site. Permitem a navegação e a utilização das suas aplicações, tal como aceder a áreas seguras do site através de login.</p>
                <p><strong>Cookies analíticas:</strong> Utilizamos estas cookies para analisar a forma como os utilizadores usam o site e monitorar a performance deste. Estas cookies são utilizadas anonimamente apenas para efeitos de criação e análise estatística.</p>
                <p><strong>Cookies de funcionalidade:</strong> Guardam as preferências do utilizador relativamente à utilização do site, de forma que não seja necessário voltar a configurar o site cada vez que o visita.</p>

                <h4 style={{ color: 'var(--text)', marginTop: '24px', marginBottom: '12px' }}>Como pode gerir as Cookies?</h4>
                <p>Depois de autorizar o uso de Cookies, o utilizador pode sempre desativar parte ou a totalidade das nossas cookies. Pode configurar as Cookies no menu “opções” ou “preferências” do seu browser.</p>
                <p>Note-se que, ao desativar as cookies, pode impedir que alguns serviços da web funcionem corretamente, afetando parcial ou totalmente a navegação.</p>
                
                <p style={{ marginTop: '24px', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '16px' }}>
                  Mais informação sobre cookies em: <a href="http://www.allaboutcookies.org" target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', textDecoration: 'underline' }}>www.allaboutcookies.org</a>
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* FLOATING COOKIE BANNER (RGPD) */}
      <AnimatePresence>
        {showCookieBanner && (
          <motion.div 
            className="cookie-banner glass-panel-heavy"
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="cookie-banner-content">
              <p>Utilizamos cookies para assegurar o funcionamento e a segurança do site (RGPD). Ao fechar este aviso ou continuar a navegação, concorda com a nossa <button onClick={() => setCookieModalOpen(true)} className="inline-link">Política de Privacidade</button>.</p>
              <button className="btn-primary-small" onClick={acceptCookies}>Aceitar</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default HeroContainer;
